import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { VoidShipmentResponse } from "~/shared/schemas/shipment-management-schema.ts";
import { voidShipmentResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postVoidValidation = async (
	poNumber: number,
	reason = "string",
) => {
	const url = apiRoutes.POSTVOIDVALIDATION.replace(
		":poNumber",
		poNumber.toString(),
	);
	const resp = await postAndValidate(
		url,
		{
			reason,
			acknowledgeMLS: true,
			acknowledgeEDI: true,
			acknowledgeTrackingOrder: true,
		},
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const postVoidShipment = async (
	poNumber: number,
	reason = "string",
): Promise<VoidShipmentResponse> => {
	const url = apiRoutes.POSTVOIDUNVOIDSHIPMENT.replace(
		":poNumber",
		poNumber.toString(),
	);
	const resp = await postAndValidate(
		url,
		{
			reason,
			acknowledgeMLS: true,
			acknowledgeEDI: true,
			acknowledgeTrackingOrder: true,
		},
		voidShipmentResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostVoidShipment = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		VoidShipmentResponse,
		Error,
		{
			poNumber: number;
			reason?: string;
		}
	>({
		mutationKey: ["voidShipment"],
		mutationFn: async ({
			poNumber,
			reason,
		}: {
			poNumber: number;
			reason?: string;
			estimatedDate?: string;
		}) => {
			await postVoidValidation(poNumber, reason);
			return postVoidShipment(poNumber, reason);
		},
		onSuccess: async (resp) => {
			// Await the refetch so the mutation stays pending (and the spinner
			// stays visible) until the shipment data has finished reloading.
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Shipment voided.");
			return resp;
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to void shipment. Please try again.",
			);
		},
	});

	return {
		voidShipment: mutate,
		voidShipmentAsync: mutateAsync,
		isSavingVoidShipment: isPending,
		error,
		data,
		resetError: reset,
	};
};
